import { ObsessedEngineeringComponent, ObsessedEngineeringComponentType } from '../models/obsessed-engineering-component.model';
import { ObsessedEngineeringProductBomLineItem } from '../models/obsessed-engineering-product-bom-line-item.model';

/** Manufacturing cost per finished product, calculated from its own version's Metals Supplied BOM. */
export function calculateObsessedEngineeringManufacturingCost(
    productVersionId: string,
    bomLineItems: ObsessedEngineeringProductBomLineItem[],
    components: ObsessedEngineeringComponent[],
): number {
    const componentById = new Map(components.map((component) => [component.id, component]));
    let totalCents = 0;
    for (const line of bomLineItems) {
        if (line.productVersionId !== productVersionId) continue;
        const component = componentById.get(line.componentId);
        if (!component) throw new Error(`BOM component ${line.componentId} is unavailable. Restore it before calculating manufacturing cost.`);
        if (component.type === ObsessedEngineeringComponentType.CustomerSupplied) continue;
        const name = component.customerPartNumber || component.partNumber;
        if (component.type !== ObsessedEngineeringComponentType.MetalsSupplied) {
            throw new Error(`${name} has an unknown supply type.`);
        }
        if (!Number.isFinite(line.quantity) || line.quantity < 0) throw new Error(`${name} has an invalid BOM quantity.`);
        if (line.quantity === 0) continue;
        const cost = component.manufacturingCost;
        if (cost === null || cost === undefined || !Number.isFinite(cost) || cost < 0) {
            throw new Error(`Set a manufacturing cost for Metals Supplied component ${name}.`);
        }
        const unitCents = Math.round((cost + Number.EPSILON) * 100);
        totalCents += unitCents * line.quantity;
    }
    // Round the BOM sum once, including fractional quantities such as feet or inches.
    const roundedCents = Math.round(totalCents + Number.EPSILON * Math.abs(totalCents));
    if (!Number.isSafeInteger(roundedCents) || roundedCents < 0) throw new Error('The BOM manufacturing cost exceeds the supported range.');
    return roundedCents / 100;
}
